import { EMPTY_VALUE } from "staticConfiguration";
import type { AlertProps, ContainerProps } from "@mui/material";
import { useBrcyCode } from "core/hooks/useBrcyCode";
import { useCentrixApi } from "core/hooks/useCentrixApi";
import { useCentrixFetch } from "core/hooks/useCentrixFetch";
import { useLocale } from "core/hooks/useLocale";
import { useToaster } from "core/hooks/useToaster";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { PitstopAlertDisplay } from "./PitstopAlertDisplay";

export function PitstopAlertBanner({
  alertProps,
  containerProps,
}: {
  alertProps?: AlertProps;
  containerProps?: ContainerProps;
}) {
  const { t } = useTranslation("Fixhub", {
    keyPrefix: "Next:Core:PitstopAlertBanner",
  });
  const brcyCode = useBrcyCode();
  const locale = useLocale();
  const centrixFetch = useCentrixFetch();
  const { errorToast } = useToaster();

  const { data, mutate } = useCentrixApi({
    path: "/api/app/pitstop-alert/active-alert",
    parameters: { query: { brcyCode, locale } },
    swrOptions: { errorRetryCount: 0 },
  });

  const dismissErrorMessage = t("dismissError", "Failed to dismiss alert");

  const handleDismiss = useCallback(async () => {
    if (!data?.id) return;
    const res = await centrixFetch({
      method: "post",
      path: "/api/app/pitstop-alert/dismiss-alert/{alertId}",
      parameters: { path: { alertId: data.id } },
    });
    if (!res.ok) {
      errorToast(dismissErrorMessage);
      return;
    }
    await mutate();
  }, [centrixFetch, data?.id, dismissErrorMessage, errorToast, mutate]);

  if (!data?.id || data.isDismissed || !data.alertType) {
    return null;
  }

  const alertType = data.alertType;
  return (
    <PitstopAlertDisplay
      alertType={alertType}
      title={data.title || EMPTY_VALUE}
      description={data.description}
      linkHref={data.linkHref}
      linkText={data.linkText}
      onClose={handleDismiss}
      alertProps={alertProps}
      containerProps={containerProps}
    />
  );
}
